import { useState, useEffect, useCallback, useRef } from "react";
import { useAppContext } from "../functions/ContextAPI";

/**
 * Custom hook for managing score form state
 * Handles score updates, validation, and auto-save
 *
 * @param {object} initialScores - Initial score values
 * @param {function} onAutoSave - Auto-save callback function
 * @param {number} debounceDelay - Debounce delay in milliseconds (default: 2000)
 */
const useScoreForm = (initialScores = {}, onAutoSave = null, debounceDelay = 2000) => {
  const context = useAppContext();
  const [scores, setScores] = useState(initialScores);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const autoSaveTimerRef = useRef(null);

  // Score field definitions with max scores
  const scoreFields = [
    { key: "academicContent", label: "Academic Matters", required: true, maxScore: 37 },
    { key: "staffing", label: "Staffing", required: true, maxScore: 40 },
    { key: "physicalFacilities", label: "Physical Facilities", required: true, maxScore: 27 },
    { key: "library", label: "Library", required: true, maxScore: 18 },
    { key: "funding", label: "Funding", required: true, maxScore: 3 },
    { key: "researchCollaboration", label: "Research & Collaborations", required: true, maxScore: 3 },
    { key: "tracerEmployer", label: "Tracer & Employers' Rating", required: true, maxScore: 2 },
  ];

  // Validate a single score value
  const validateScore = useCallback((value, fieldName, required = true, maxScore = 100) => {
    if (value === "" || value === null || value === undefined) {
      if (required) {
        return `${fieldName} is required`;
      }
      return null;
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      return "Please enter a valid number";
    }

    if (numValue < 0) {
      return "Score cannot be less than 0";
    }

    if (numValue > maxScore) {
      return `Score cannot be greater than ${maxScore}`;
    }

    return null;
  }, []);

  // Validate all scores
  const validateAllScores = useCallback(() => {
    const newErrors = {};
    let valid = true;

    scoreFields.forEach((field) => {
      const error = validateScore(
        scores[field.key],
        field.label,
        field.required,
        field.maxScore
      );
      if (error) {
        newErrors[field.key] = error;
        valid = false;
      }
    });

    setErrors(newErrors);
    setIsValid(valid);
    return valid;
  }, [scores, validateScore]);

  // Update a single score
  const updateScore = useCallback(
    (field, value) => {
      setScores((prev) => ({
        ...prev,
        [field]: value,
      }));

      setTouched((prev) => ({
        ...prev,
        [field]: true,
      }));

      setIsDirty(true);

      // Update context
      context.updateScores(field, value);

      // Validate the field
      const fieldConfig = scoreFields.find((f) => f.key === field);
      if (fieldConfig) {
        const error = validateScore(value, fieldConfig.label, fieldConfig.required, fieldConfig.maxScore);
        setErrors((prev) => ({
          ...prev,
          [field]: error,
        }));
      }
    },
    [context, validateScore]
  );

  // Batch update scores
  const updateScores = useCallback((newScores) => {
    setScores((prev) => ({
      ...prev,
      ...newScores,
    }));
    setIsDirty(true);
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    setScores(initialScores);
    setErrors({});
    setTouched({});
    setIsValid(false);
    setIsDirty(false);
  }, [initialScores]);

  // Mark field as touched
  const touchField = useCallback((field) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  }, []);

  // Check if all fields are filled
  const areAllFieldsFilled = useCallback(() => {
    return scoreFields.every((field) => {
      const value = scores[field.key];
      return value !== "" && value !== null && value !== undefined;
    });
  }, [scores]);

  // Calculate total score
  const calculateTotal = useCallback(() => {
    return scoreFields.reduce((total, field) => {
      const value = parseFloat(scores[field.key] || 0);
      return total + (isNaN(value) ? 0 : value);
    }, 0);
  }, [scores]);

  // Get score summary
  const getScoreSummary = useCallback(() => {
    return {
      total: calculateTotal(),
      filled: scoreFields.filter((field) => {
        const value = scores[field.key];
        return value !== "" && value !== null && value !== undefined;
      }).length,
      totalFields: scoreFields.length,
      isComplete: areAllFieldsFilled(),
      isValid: isValid && areAllFieldsFilled(),
    };
  }, [scores, isValid, areAllFieldsFilled, calculateTotal]);

  // Auto-save effect with debouncing
  useEffect(() => {
    if (isDirty && onAutoSave) {
      // Clear existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      // Set new timer
      autoSaveTimerRef.current = setTimeout(() => {
        onAutoSave(scores);
        setIsDirty(false);
      }, debounceDelay);
    }

    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [scores, isDirty, onAutoSave, debounceDelay]);

  // Validate on mount and when scores change
  useEffect(() => {
    validateAllScores();
  }, [scores, validateAllScores]);

  return {
    scores,
    errors,
    touched,
    isValid,
    isDirty,
    scoreFields,
    updateScore,
    updateScores,
    resetForm,
    touchField,
    validateScore,
    validateAllScores,
    areAllFieldsFilled,
    calculateTotal,
    getScoreSummary,
  };
};

export default useScoreForm;